import React from "react";
import { Link } from "react-router-dom";
import { LineChart, Mail, Phone, MapPin } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

export default function Footer() {
  const { t, buildPath, lang } = useI18n();
  const homePath = buildPath("");
  const anchorHref = (hash) => `${homePath}${hash}`;

  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-4">
          <div className="md:col-span-1">
            <Link to={homePath} className="flex items-center gap-2 text-white">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-teal-500 text-slate-900">
                <LineChart className="h-5 w-5" strokeWidth={2.25} />
              </span>
              <span className="font-jakarta text-lg font-bold tracking-tight">
                {t("brand.name")}
              </span>
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              {t("footer.tagline")}
            </p>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider">
              {t("footer.columns.product")}
            </h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <a href={anchorHref("#about")} data-testid="footer-link-about" className="hover:text-white transition-colors">
                  {t("footer.links.about")}
                </a>
              </li>
              <li>
                <a href={anchorHref("#how")} data-testid="footer-link-how" className="hover:text-white transition-colors">
                  {t("footer.links.how")}
                </a>
              </li>
              <li>
                <a href={anchorHref("#advantages")} data-testid="footer-link-advantages" className="hover:text-white transition-colors">
                  {t("footer.links.advantages")}
                </a>
              </li>
              <li>
                <a href={anchorHref("#faq")} data-testid="footer-link-faq" className="hover:text-white transition-colors">
                  {t("footer.links.faq")}
                </a>
              </li>
              <li>
                <a href={anchorHref("#newsletter")} data-testid="footer-link-newsletter" className="hover:text-white transition-colors">
                  {t("footer.links.newsletter")}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider">
              {t("footer.columns.legal")}
            </h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li>
                <Link
                  to={buildPath("privacy-policy")}
                  data-testid="footer-legal-privacy"
                  className="hover:text-white transition-colors"
                >
                  {t("footer.links.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={buildPath("cookie-policy")}
                  data-testid="footer-legal-cookies"
                  className="hover:text-white transition-colors"
                >
                  {t("footer.links.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={buildPath("terms")}
                  data-testid="footer-legal-terms"
                  className="hover:text-white transition-colors"
                >
                  {t("footer.links.terms")}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm uppercase tracking-wider">
              {t("footer.columns.contact")}
            </h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 text-teal-400 shrink-0" />
                <span>{t("footer.legal.address_value")}</span>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-teal-400 shrink-0" />
                <a href={`mailto:${t("footer.legal.email_value")}`} className="hover:text-white">
                  {t("footer.legal.email_value")}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-teal-400 shrink-0" />
                <a href={`tel:${t("footer.legal.phone_value").replace(/\s+/g, "")}`} className="hover:text-white">
                  {t("footer.legal.phone_value")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-slate-800 pt-8 text-xs text-slate-400 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            <p>
              <span className="text-slate-500">{t("footer.legal.company_name")}</span>
            </p>
            <p>
              <span className="text-slate-500">{t("footer.legal.ruc_label")}</span>{" "}
              <span className="text-slate-300">{t("footer.legal.ruc_value")}</span>
            </p>
            <p>
              <span className="text-slate-500">{t("footer.legal.supercias_label")}</span>{" "}
              <span className="text-slate-300">{t("footer.legal.supercias_value")}</span>
            </p>
            <p>
              <span className="text-slate-500">{t("footer.legal.incorporation_label")}</span>{" "}
              <span className="text-slate-300">{t("footer.legal.incorporation_value")}</span>
            </p>
            <p>
              <span className="text-slate-500">{t("footer.legal.jurisdiction_label")}</span>{" "}
              <span className="text-slate-300">{t("footer.legal.jurisdiction_value")}</span>
            </p>
            <p>
              <span className="text-slate-500">{t("footer.legal.license_label")}</span>{" "}
              <span className="text-slate-300">{t("footer.legal.license_value")}</span>
            </p>
          </div>
          <p className="pt-4 text-slate-500">{t("footer.disclaimer")}</p>
          <p className="text-slate-500">{t("footer.copyright")}</p>
        </div>
      </div>
    </footer>
  );
}
